/**
 * App.tsx - Orchestrator
 *
 * This is the main entry component that composes all hooks and views.
 * After refactoring, it only handles:
 * 1. Module-level setup (polyfill, PWA event, self-embed check)
 * 2. Calling hooks to get state
 * 3. Rendering EntryView or CanvasView based on currentView
 *
 * See REFACTOR_ARCHITECTURE.md for the full architecture overview.
 */

import { useEditorInterface } from "@excalidraw/excalidraw";
import { isRunningInIframe } from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useEffect, useRef, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { Provider, appJotaiStore } from "./app-jotai";

import { TopErrorBoundary } from "./components/TopErrorBoundary";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";
import { PrivacyPage } from "./components/PrivacyPage";
import { TermsPage } from "./components/TermsPage";

import "./index.scss";
import "./components/IdeaWeaveCanvas.scss";

// Hooks
import { useHandleAppTheme } from "./useHandleAppTheme";
import { useAppLangCode } from "./app-language/language-state";
import { useSupabaseAuth } from "./hooks/useSupabaseAuth";
import { useDiagramSync } from "./hooks/useDiagramSync";
import { useAppView } from "./hooks/useAppView";
import { useAuthGuard } from "./hooks/useAuthGuard";
import { useProjectManager } from "./hooks/useProjectManager";
import { useSceneInit } from "./hooks/useSceneInit";
import { useCollaboration } from "./hooks/useCollaboration";
import { useCanvasActions } from "./hooks/useCanvasActions";
import { useAppI18n } from "./hooks/useAppI18n";

// Views
import { EntryView } from "./views/EntryView";
import { CanvasView } from "./views/CanvasView";

// Utils
import {
  getUserAvatarUrl,
  getUserDisplayName,
  getUserInitials,
  readUserDisplayCache,
  writeUserDisplayCache,
  clearUserDisplayCache,
} from "./utils/userProfile";

// ─── Module-level setup ─────────────────────────────────────────────────────

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

const getPwaEvent = () => pwaEvent;
const setPwaEvent = (event: BeforeInstallPromptEvent | null) => {
  pwaEvent = event;
};

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    event.preventDefault();
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

// ─── Main Component ─────────────────────────────────────────────────────────

const ExcalidrawWrapper = () => {
  const isCollabDisabled = isRunningInIframe();

  // ── Theme & Language ──
  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();
  const [langCode, setLangCode] = useAppLangCode();
  const editorInterface = useEditorInterface();
  const { t } = useAppI18n();

  // ── Auth ──
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const auth = useSupabaseAuth();

  // ── Diagram Sync ──
  const diagramSync = useDiagramSync({ session: auth.session });

  // ── User Info (with localStorage cache for instant display) ──
  // useState (lazy init) instead of useMemo: allows cache to be reactively
  // cleared on sign-out so the stale avatar doesn't persist in memory.
  const [displayCache, setDisplayCache] = useState(() =>
    readUserDisplayCache(),
  );

  const liveDisplayName = getUserDisplayName(
    auth.profile,
    auth.session?.user ?? null,
  );
  const liveAvatarUrl = getUserAvatarUrl(
    auth.profile,
    auth.session?.user ?? null,
  );
  const liveInitials = auth.session?.user
    ? getUserInitials(liveDisplayName)
    : undefined;

  // Use live data when available; fall back to cache during the
  // gap between "session ready" and "profile loaded".
  const profileReady = Boolean(auth.profile);
  const userDisplayName = profileReady
    ? liveDisplayName
    : liveDisplayName !== "there"
    ? liveDisplayName
    : displayCache?.displayName ?? liveDisplayName;
  const userAvatarUrl = profileReady
    ? liveAvatarUrl
    : liveAvatarUrl ?? displayCache?.avatarUrl ?? null;
  const userInitials = profileReady
    ? liveInitials
    : liveInitials ?? displayCache?.initials ?? undefined;
  const userLabel = auth.session?.user
    ? userDisplayName
    : t("auth.signInSignUp");

  // Persist display cache when profile loads; clear on logout.
  useEffect(() => {
    if (auth.session?.user && auth.profile) {
      const name = getUserDisplayName(auth.profile, auth.session.user);
      const newCache = {
        displayName: name,
        avatarUrl: getUserAvatarUrl(auth.profile, auth.session.user),
        initials: getUserInitials(name),
      };
      writeUserDisplayCache(newCache);
      setDisplayCache(newCache);
    } else if (!auth.loading && !auth.session?.user) {
      clearUserDisplayCache();
      setDisplayCache(null);
    }
  }, [auth.loading, auth.session?.user, auth.profile]);

  // ── App View ──
  const {
    currentView,
    aiSidebarCollapsed,
    setAiSidebarCollapsed,
    initialAIPrompt,
    initialPlan,
    initialConversationContext,
    clearInitialConversationContext,
    isProjectShelfOpen,
    setIsProjectShelfOpen,
    handleWeaveIdea,
    handleDrawDirect,
    handleStartCanvas,
    handleBackToEntry,
    handleNewProject,
    handleSelectProjectFromEntry,
    handleGoToDrawFromConversation,
    handleBackFromConversation,
    handleBackToConversation,
    isConversationActive,
    conversationTopic,
    conversationInitialImageUrls,
    conversationInitialFiles,
    projectAction,
    clearProjectAction,
    hasPendingConversationFromEntry,
    initialDrawImageUrls,
    initialDrawFiles,
    setInitialDrawImageUrls,
    setInitialDrawFiles,
  } = useAppView();

  // ── Auth Guard ──
  useAuthGuard({
    session: auth.session,
    loading: auth.loading,
    currentView,
    handleBackToEntry,
  });

  // ── Collaboration ──
  const {
    collabAPI,
    isCollaborating,
    collabError,
    isOffline,
    localStorageQuotaExceeded,
    setShareDialogState,
    onCollabDialogOpen,
  } = useCollaboration();

  // ── Shared excalidrawAPI ref ──
  // This ref is shared between useProjectManager and useSceneInit so that
  // both can access the API without creating circular dependencies.
  const excalidrawAPIRef = useRef<ExcalidrawImperativeAPI | null>(null);

  // ── Project Manager ──
  // Must run before useSceneInit so that initialStatePromiseRef is set up
  // synchronously for new projects.
  const { excalidrawKey, initialStatePromiseRef } = useProjectManager({
    excalidrawAPIRef,
    projectAction,
    clearProjectAction,
    currentView,
    isAuthLoading: auth.loading,
    isAuthenticated: Boolean(auth.session?.user),
    currentDiagramId: diagramSync.currentDiagramId,
    loadDiagram: diagramSync.loadDiagram,
  });

  // ── Scene Init ──
  const {
    excalidrawAPI,
    excalidrawRefCallback,
    isExcalidrawReady,
    debugCanvasRef,
    forceRefresh,
  } = useSceneInit({
    isCollabDisabled,
    collabAPI,
    initialStatePromiseRef,
    setLangCode,
    currentView,
  });

  // Keep the shared ref in sync with the actual API
  excalidrawAPIRef.current = excalidrawAPI;

  // ── Canvas Actions ──
  const {
    currentZoom,
    latestShareableLink,
    setLatestShareableLink,
    errorMessage,
    setErrorMessage,
    onChange,
    onExportToBackend,
    renderCustomStats,
    handleExport,
    handleZoomIn,
    handleZoomOut,
    handleUndo,
    handleRedo,
  } = useCanvasActions({
    excalidrawAPI,
    collabAPI,
    authSession: auth.session,
    diagramSync,
    debugCanvasRef,
  });

  // ── Self-embedding guard ──
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  // ── Entry View ──
  if (currentView === "entry") {
    return (
      <EntryView
        userDisplayName={userDisplayName}
        userAvatarUrl={userAvatarUrl}
        userInitials={userInitials}
        onUserClick={() => setIsAuthModalOpen(true)}
        onWeaveIdea={handleWeaveIdea}
        onDrawDirect={handleDrawDirect}
        onStartCanvas={handleStartCanvas}
        onSelectProject={handleSelectProjectFromEntry}
        recentDiagrams={diagramSync.diagrams.slice(0, 4)}
        isLoadingRecent={diagramSync.isLoadingList}
        isAuthenticated={Boolean(auth.session?.user)}
        isAuthLoading={
          auth.loading
            ? !displayCache // still fetching session – show loading unless cache available
            : Boolean(auth.session?.user) &&
              auth.profileLoading &&
              !displayCache
        }
        isDark={editorTheme === "dark"}
        isConversationActive={isConversationActive}
        conversationTopic={conversationTopic}
        conversationInitialImageUrls={conversationInitialImageUrls}
        conversationInitialFiles={conversationInitialFiles}
        onBackFromConversation={handleBackFromConversation}
        onGoToDraw={handleGoToDrawFromConversation}
        userId={auth.session?.user?.id ?? null}
        isAuthModalOpen={isAuthModalOpen}
        onCloseAuthModal={() => setIsAuthModalOpen(false)}
        auth={auth}
      />
    );
  }

  // ── Canvas View ──
  return (
    <CanvasView
      excalidrawKey={excalidrawKey}
      excalidrawRefCallback={excalidrawRefCallback}
      excalidrawAPI={excalidrawAPI}
      initialData={initialStatePromiseRef.current.promise}
      editorTheme={editorTheme}
      langCode={langCode}
      appTheme={appTheme}
      setAppTheme={setAppTheme}
      editorInterface={editorInterface}
      isCollabDisabled={isCollabDisabled}
      isCollaborating={isCollaborating}
      collabAPI={collabAPI}
      collabError={collabError}
      isOffline={isOffline}
      localStorageQuotaExceeded={localStorageQuotaExceeded}
      setShareDialogState={setShareDialogState}
      onCollabDialogOpen={onCollabDialogOpen}
      onChange={onChange}
      onExportToBackend={onExportToBackend}
      renderCustomStats={renderCustomStats}
      handleExport={handleExport}
      handleZoomIn={handleZoomIn}
      handleZoomOut={handleZoomOut}
      handleUndo={handleUndo}
      handleRedo={handleRedo}
      currentZoom={currentZoom}
      latestShareableLink={latestShareableLink}
      setLatestShareableLink={setLatestShareableLink}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      aiSidebarCollapsed={aiSidebarCollapsed}
      setAiSidebarCollapsed={setAiSidebarCollapsed}
      initialAIPrompt={initialAIPrompt}
      initialPlan={initialPlan}
      initialConversationContext={initialConversationContext}
      onConversationContextUsed={clearInitialConversationContext}
      initialDrawImageUrls={initialDrawImageUrls}
      initialDrawFiles={initialDrawFiles}
      onDrawAttachmentsUsed={() => {
        setInitialDrawImageUrls(undefined);
        setInitialDrawFiles(undefined);
      }}
      isExcalidrawReady={isExcalidrawReady}
      isProjectShelfOpen={isProjectShelfOpen}
      setIsProjectShelfOpen={setIsProjectShelfOpen}
      handleNewProject={handleNewProject}
      diagramSync={diagramSync}
      isAuthenticated={Boolean(auth.session?.user)}
      auth={auth}
      isAuthModalOpen={isAuthModalOpen}
      onCloseAuthModal={() => setIsAuthModalOpen(false)}
      onUserClick={() => setIsAuthModalOpen(true)}
      userLabel={userLabel}
      userAvatarUrl={userAvatarUrl}
      userInitials={userInitials}
      getPwaEvent={getPwaEvent}
      setPwaEvent={setPwaEvent}
      debugCanvasRef={debugCanvasRef}
      forceRefresh={forceRefresh}
      hasPendingConversation={hasPendingConversationFromEntry}
      onBackToConversation={handleBackToConversation}
    />
  );
};

// ─── App Root ───────────────────────────────────────────────────────────────

const ExcalidrawApp = () => {
  const pathname = window.location.pathname;
  const normalizedPath = pathname.startsWith("/app")
    ? pathname.slice(4) || "/"
    : pathname;

  const isCloudExportWindow = normalizedPath === "/excalidraw-plus-export";
  const isPrivacyPage = normalizedPath === "/privacy";
  const isTermsPage = normalizedPath === "/terms";

  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }
  if (isPrivacyPage) {
    return <PrivacyPage />;
  }
  if (isTermsPage) {
    return <TermsPage />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
